import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    FormGroup,
    Form,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    getOverrideMasterSettings,
    updateOverrideMasterSettings,
} from "model/MasterSettingModel";
import Loaders from "components/Common/Loaders";

const initialFormState = {
    id: "",
    overrideClientReportMailInterval: false,
    overrideAIRecruiterEnabled: false,
    updatedOn: "",
    updatedByUserId: "",
    displayName: "",
};

const getLoggedInUserId = () => {
    try {
        const raw = localStorage.getItem("userData");
        if (!raw) return "";
        const user = JSON.parse(raw);
        const nested = user?.user && typeof user.user === "object" ? user.user : null;
        return String(
            user?.userId ??
            user?.id ??
            user?.Id ??
            nested?.userId ??
            nested?.id ??
            nested?.Id ??
            ""
        ).trim();
    } catch (_error) {
        return "";
    }
};

const OverrideMasterSettingsModal = ({
    overrideMasterSettingsModalOpen,
    setOverrideMasterSettingsModalOpen,
}) => {
    const [formData, setFormData] = useState(initialFormState);
    const [formKey, setFormKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const closeModal = () => {
        setOverrideMasterSettingsModalOpen(false);
    };

    const fetchOverrideMasterSettings = async () => {
        setLoading(true);
        try {
            const response = await getOverrideMasterSettings({});
            const data = response?.data || {};
            setFormData({
                id: String(data?.id ?? ""),
                overrideClientReportMailInterval: Boolean(data?.overrideClientReportMailInterval),
                overrideAIRecruiterEnabled: Boolean(data?.overrideAIRecruiterEnabled),
                updatedOn: data?.updatedOn ?? "",
                updatedByUserId: String(data?.updatedByUserId ?? getLoggedInUserId()),
                displayName: data?.name ?? data?.displayName ?? data?.companyName ?? "",
            });
            setFormKey((key) => key + 1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (overrideMasterSettingsModalOpen) {
            fetchOverrideMasterSettings();
        }
    }, [overrideMasterSettingsModalOpen]);

    const handleCheckboxChange = (fieldName, checked) => {
        setFormData((previous) => ({
            ...previous,
            [fieldName]: checked,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = {
                id: String(formData?.id ?? ""),
                overrideClientReportMailInterval: Boolean(formData?.overrideClientReportMailInterval),
                overrideAIRecruiterEnabled: Boolean(formData?.overrideAIRecruiterEnabled),
                updatedOn: new Date().toISOString(),
                updatedByUserId: String(formData?.updatedByUserId || getLoggedInUserId()),
            };
            const response = await updateOverrideMasterSettings(payload);
            if (response?.success) {
                toast.success(response?.message || "Settings saved successfully");
                closeModal();
            } else {
                toast.error(response?.message || "Unable to save settings");
            }
        } finally {
            setSaving(false);
        }
    };

    const modalTitleSuffix = formData?.displayName
        ? ` - ${String(formData.displayName).toUpperCase()}`
        : "";

    return (
        <Modal
            isOpen={overrideMasterSettingsModalOpen}
            toggle={closeModal}
            backdrop="static"
            className="modal-full-height"
            centered
            zIndex={5000}
        >
            <ModalBody
                key={formKey}
                className="p-4"
                style={{ maxHeight: "95vh", overflowY: "auto" }}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 text-uppercase">
                        {`Override Master Settings${modalTitleSuffix}`}
                    </h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr />

                {loading ? (
                    <Loaders />
                ) : (
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <div className="input-box d-flex align-items-center justify-content-between">
                                <span>Override Client Report Mail Interval</span>
                                <Input
                                    type="checkbox"
                                    className="form-check-input"
                                    defaultChecked={formData?.overrideClientReportMailInterval}
                                    onChange={(event) =>
                                        handleCheckboxChange(
                                            "overrideClientReportMailInterval",
                                            event.target.checked
                                        )
                                    }
                                />
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <div className="input-box d-flex align-items-center justify-content-between">
                                <span>Override AI Recruiter Enabled</span>
                                <Input
                                    type="checkbox"
                                    className="form-check-input"
                                    defaultChecked={formData?.overrideAIRecruiterEnabled}
                                    onChange={(event) =>
                                        handleCheckboxChange(
                                            "overrideAIRecruiterEnabled",
                                            event.target.checked
                                        )
                                    }
                                />
                            </div>
                        </FormGroup>

                        <div className="text-end mt-3">
                            <Button color="primary" type="submit" disabled={saving}>
                                Save
                            </Button>
                        </div>
                    </Form>
                )}
            </ModalBody>
        </Modal>
    );
};

export default OverrideMasterSettingsModal;
